import { Product, SortOption, ProductFilterState } from '../types';

/**
 * A product behaves as apparel when it carries apparel data — color variants,
 * a stock matrix, or explicit sizes. Category names are admin-editable, so
 * they can no longer be used to infer apparel behavior.
 */
export const isApparelProduct = (product: Product): boolean =>
  !!(
    (product.colors && product.colors.length > 0) ||
    (product.variants && product.variants.length > 0) ||
    (product.sizes && product.sizes.length > 0)
  );

/**
 * Filters products based on category, riding style, and price.
 * Pure function: strictly inputs -> outputs, no side effects.
 */
export const filterProducts = (
  products: Product[],
  filters: ProductFilterState
): Product[] => {
  return products.filter((product) => {
    // Category Filter
    if (filters.category.length > 0 && !filters.category.includes(product.category)) {
      return false;
    }

    // Riding Style Filter (if product has riding styles)
    if (filters.ridingStyle.length > 0) {
      if (!product.ridingStyle) return false;
      const hasMatchingStyle = product.ridingStyle.some(style => 
        filters.ridingStyle.includes(style)
      );
      if (!hasMatchingStyle) return false;
    }

    // Price Filter
    if (product.price < filters.priceRange[0] || product.price > filters.priceRange[1]) {
      return false;
    }

    return true;
  });
};

/**
 * Fisher-Yates shuffle. Returns a new array; input is not mutated.
 * Used to randomise the default (unfiltered, 'featured') shop presentation
 * per visit — explicit sort options still apply on top.
 */
export const shuffleProducts = <T,>(items: T[]): T[] => {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Sorts products based on the selected option.
 */
export const sortProducts = (
  products: Product[],
  option: SortOption
): Product[] => {
  const sorted = [...products]; // Create a copy to maintain immutability

  switch (option) {
    case 'price-asc':
      return sorted.sort((a, b) => a.price - b.price);
    case 'price-desc':
      return sorted.sort((a, b) => b.price - a.price);
    case 'newest':
      return sorted.sort((a, b) => (b.isNew === a.isNew) ? 0 : b.isNew ? 1 : -1);
    case 'featured':
    default:
      return sorted;
  }
};

/**
 * Extracts unique available values for filters from the product list.
 */
export const getAvailableFilters = (products: Product[]) => {
  const categories = Array.from(new Set(products.map(p => p.category))).sort();
  const ridingStyles = Array.from(new Set(products.flatMap(p => p.ridingStyle || []))).sort();
  
  const prices = products.map(p => p.price);
  const minPrice = prices.length > 0 ? Math.floor(Math.min(...prices)) : 0;
  const maxPrice = prices.length > 0 ? Math.ceil(Math.max(...prices)) : 0;

  return { categories, ridingStyles, minPrice, maxPrice };
};
